// src/components/shared/forms/EntityBasicInfoForm.jsx
import React from 'react';
import { Grid, InputAdornment, Box, useTheme, alpha, Typography } from '@mui/material';
import {
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Code as CodeIcon,
  Info as InfoIcon,
  Category as CategoryIcon,
  HelpOutline as HelpIcon,
  Person as PersonIcon,
} from '@mui/icons-material';

// Import standardized form components
import { 
  MagicTextField, 
  MagicSelect,
  MagicAutocomplete
} from '../../ui/FormFields';
import { FormSection } from '../../../design-system/components/forms';

/**
 * A flexible and reusable basic information form for different entity types
 * (clients, brands, campaigns, projects)
 * 
 * @param {Object} props Component props
 * @param {Object} props.data The entity data containing form values
 * @param {Function} props.onChange Event-based change handler
 * @param {Function} props.onCustomChange Name/value based change handler
 * @param {Function} props.onNestedChange Nested field change handler
 * @param {Function} props.setFieldValue Formik-style field setter
 * @param {Object} props.errors Validation errors
 * @param {Object} props.touched Touched fields
 * @param {String} props.title Section title (default: 'Basic Information')
 * @param {String} props.description Section description
 * @param {Array} props.fields Array of field configurations
 * @param {Object} props.options Pre-configured select field options
 * @param {Boolean} props.showHeader Whether to show the FormSection header
 * @param {Object} props.relationshipField Configuration for parent entity relationship
 * @param {Object} props.nestedFields Configuration for nested fields
 */
const EntityBasicInfoForm = ({ 
  data = {},
  onChange,
  onCustomChange,
  onNestedChange,
  setFieldValue,
  errors = {}, 
  touched = {},
  title = 'Basic Information',
  description,
  fields = [],
  options = {},
  showHeader = true,
  relationshipField = null,
  nestedFields = {},
  icon = null,
}) => {
  const theme = useTheme();
  
  // Default industry options
  const defaultIndustryOptions = [
    { value: 'Luxury', label: 'Luxury' },
    { value: 'Hospitality', label: 'Hospitality' },
    { value: 'Finance', label: 'Finance' },
    { value: 'Technology', label: 'Technology' },
    { value: 'E-commerce', label: 'E-commerce' },
    { value: 'Healthcare', label: 'Healthcare' },
    { value: 'Education', label: 'Education' },
    { value: 'Real Estate', label: 'Real Estate' },
    { value: 'Food & Beverage', label: 'Food & Beverage' },
    { value: 'Automotive', label: 'Automotive' },
    { value: 'Media & Entertainment', label: 'Media & Entertainment' },
    { value: 'Manufacturing', label: 'Manufacturing' },
    { value: 'Retail', label: 'Retail' },
    { value: 'Professional Services', label: 'Professional Services' },
    { value: 'Non-Profit', label: 'Non-Profit' },
    { value: 'Government', label: 'Government' },
    { value: 'Other', label: 'Other' }
  ];

  // Default region options
  const defaultRegionOptions = [
    { value: 'Egypt', label: 'Egypt' },
    { value: 'Saudi Arabia', label: 'Saudi Arabia' },
    { value: 'Qatar', label: 'Qatar' },
    { value: 'UAE', label: 'UAE' },
    { value: 'Kuwait', label: 'Kuwait' },
    { value: 'Bahrain', label: 'Bahrain' },
    { value: 'Oman', label: 'Oman' },
    { value: 'Other', label: 'Other' }
  ];
  
  // Default status options
  const defaultStatusOptions = [
    { value: 'draft', label: 'Draft' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' }
  ];
  
  // Combine provided options with defaults
  const mergedOptions = {
    industry: options.industry || defaultIndustryOptions,
    region: options.region || defaultRegionOptions,
    status: options.status || defaultStatusOptions,
    ...options
  };
  
  // Handle text field change (multiple API patterns)
  const handleTextChange = (e) => {
    const { name, value } = e.target;
    
    if (onChange && typeof onChange === 'function') {
      // Modern event-based onChange
      if (onChange.length !== 2) {
        onChange(e);
      } 
      // Legacy name, value onChange
      else {
        onChange(name, value);
      }
    } else if (onCustomChange) {
      onCustomChange(name, value);
    } else if (setFieldValue) {
      setFieldValue(name, value);
    }
  };
  
  // Handle select field change (multiple API patterns)
  const handleSelectChange = (value, name) => {
    console.log('handleSelectChange in EntityBasicInfoForm:', { value, name });
    
    // MagicSelect passes parameters as (value, name), so we need to respect that order
    if (onChange && typeof onChange === 'function') {
      if (onChange.length === 2) {
        // Pass through with the same parameter order we received (name, value)
        console.log(`Calling onChange with (${name}, ${value})`);
        onChange(name, value);
      } else {
        // Event-based onChange with {target: {name, value}} structure
        onChange({target: {name, value}});
      }
    } else if (onCustomChange) {
      onCustomChange(name, value);
    } else if (setFieldValue) {
      setFieldValue(name, value);
    }
  };
  
  // Handle nested field change (e.g., industry.primary)
  const handleNestedChange = (parent, field, value) => {
    if (onNestedChange) {
      onNestedChange(parent, field, value);
    } else if (setFieldValue) {
      setFieldValue(`${parent}.${field}`, value);
    } else if (onChange && typeof onChange === 'function' && onChange.length === 2) {
      const updatedValue = {
        ...data[parent],
        [field]: value
      };
      onChange(parent, updatedValue);
    }
  };
  
  // Get common icon components by name
  const getIconComponent = (iconName) => {
    switch (iconName) {
      case 'business':
        return <BusinessIcon color="action" />;
      case 'location':
        return <LocationIcon color="action" />;
      case 'code':
        return <CodeIcon color="action" />;
      case 'info':
        return <InfoIcon color="action" />;
      case 'category':
        return <CategoryIcon color="action" />;
      case 'help':
        return <HelpIcon color="action" />;
      case 'person':
        return <PersonIcon color="action" />;
      default:
        return null;
    }
  };
  
  // Get field value (handles nested fields)
  const getFieldValue = (fieldName) => {
    if (fieldName.includes('.')) {
      const [parent, child] = fieldName.split('.');
      return data[parent]?.[child] !== undefined ? data[parent][child] : '';
    }
    return data[fieldName] !== undefined ? data[fieldName] : '';
  };
  
  // Default fields if none provided
  const defaultFields = [
    {
      name: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      icon: 'business',
      gridSize: { xs: 12, sm: 6 },
      placeholder: 'Enter name'
    },
    {
      name: 'description',
      label: 'Description',
      type: 'multiline',
      rows: 4,
      icon: 'info',
      gridSize: { xs: 12 },
      placeholder: 'Enter description'
    }
  ];
  
  // Use provided fields or defaults
  const formFields = fields.length > 0 ? fields : defaultFields;
  
  // Render form content
  const formContent = (
    <Grid container spacing={3}>
      {/* Render fields based on configuration */}
      {formFields.map((field, index) => (
        <Grid 
          item 
          key={`${field.name}-${index}`} 
          xs={field.gridSize?.xs || 12} 
          sm={field.gridSize?.sm} 
          md={field.gridSize?.md}
          lg={field.gridSize?.lg}
        >
          {field.type === 'custom' && field.renderField ? (
            // Custom field rendering
            field.renderField({
              field,
              value: getFieldValue(field.name),
              error: touched[field.name] && Boolean(errors[field.name]),
              helperText: touched[field.name] && errors[field.name],
              touched: touched[field.name],
              onChange: field.nested ? 
                (e) => handleNestedChange(field.nested.parent, field.nested.field, e.target.value) : 
                handleTextChange,
              onCustomChange: onCustomChange
            })
          ) : field.type === 'select' ? (
            <MagicSelect
              name={field.name}
              label={field.label}
              value={getFieldValue(field.name)}
              onChange={field.nested ? 
                (value, name) => handleNestedChange(field.nested.parent, field.nested.field, value) : 
                handleSelectChange
              }
              options={mergedOptions[field.optionsKey] || field.options || []}
              error={touched[field.name] && Boolean(errors[field.name])}
              helperText={
                field.helpText ? (
                  <>
                    {touched[field.name] && errors[field.name] ?
                      errors[field.name] :
                      <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                        <HelpIcon
                          fontSize="small"
                          sx={{
                            mr: 1,
                            color: alpha(theme.palette.primary.main, 0.7),
                            fontSize: 16,
                          }}
                        />
                        {field.helpText}
                      </Box>
                    }
                  </>
                ) : (touched[field.name] && errors[field.name])
              }
              required={field.required}
              magical={true}
              formControlProps={{
                fullWidth: true,
                ...field.formControlProps
              }}
              placeholder={field.placeholder}
              InputProps={{
                startAdornment: field.icon ? (
                  <InputAdornment position="start">
                    {typeof field.icon === 'string' ? getIconComponent(field.icon) : field.icon}
                  </InputAdornment>
                ) : undefined,
                ...field.InputProps
              }}
              disabled={field.disabled}
            />
          ) : field.type === 'autocomplete' ? (
            <MagicAutocomplete
              name={field.name}
              label={field.label}
              value={getFieldValue(field.name) || []}
              onChange={(newValue, name) => {
                if (field.nested) {
                  handleNestedChange(field.nested.parent, field.nested.field, newValue);
                } else if (onCustomChange) {
                  onCustomChange(name, newValue);
                } else {
                  onChange(name, newValue);
                }
              }}
              options={field.autocompleteProps?.options || []}
              multiple={field.autocompleteProps?.multiple || false}
              freeSolo={field.autocompleteProps?.freeSolo || false}
              error={touched[field.name] && Boolean(errors[field.name])}
              helperText={
                field.helpText ? (
                  <>
                    {touched[field.name] && errors[field.name] ?
                      errors[field.name] :
                      <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                        <HelpIcon
                          fontSize="small"
                          sx={{
                            mr: 1,
                            color: alpha(theme.palette.primary.main, 0.7),
                            fontSize: 16,
                          }}
                        />
                        {field.helpText}
                      </Box>
                    }
                  </>
                ) : (touched[field.name] && errors[field.name])
              }
              required={field.required}
              placeholder={field.placeholder}
              textFieldProps={{
                InputProps: {
                  startAdornment: field.icon ? (
                    <InputAdornment position="start">
                      {typeof field.icon === 'string' ? getIconComponent(field.icon) : field.icon}
                    </InputAdornment>
                  ) : undefined,
                  ...field.InputProps
                }
              }}
              disabled={field.disabled}
              {...field.autocompleteProps}
            />
          ) : field.type === 'multiline' ? (
            <MagicTextField
              label={field.label}
              name={field.name}
              value={getFieldValue(field.name)}
              onChange={field.nested ? 
                (e) => handleNestedChange(field.nested.parent, field.nested.field, e.target.value) : 
                handleTextChange
              }
              multiline
              rows={field.rows || 4}
              fullWidth
              placeholder={field.placeholder}
              error={touched[field.name] && Boolean(errors[field.name])}
              helperText={touched[field.name] && errors[field.name]}
              InputProps={{
                startAdornment: field.icon ? (
                  <InputAdornment position="start">
                    {typeof field.icon === 'string' ? getIconComponent(field.icon) : field.icon}
                  </InputAdornment>
                ) : undefined,
                ...field.InputProps
              }}
              disabled={field.disabled}
            />
          ) : field.type === 'relationship' ? (
            <MagicSelect
              name={field.name}
              label={field.label}
              value={
                // Handle different value patterns for relationship fields
                typeof data[field.name] === 'object' ? 
                  data[field.name]?._id || data[field.name]?.id || '' : 
                  data[field.name] || ''
              }
              onChange={handleSelectChange}
              options={field.options || []}
              error={touched[field.name] && Boolean(errors[field.name])}
              helperText={
                field.disabled ? field.disabledHelperText :
                (touched[field.name] && errors[field.name])
              }
              required={field.required}
              magical={true}
              formControlProps={{
                fullWidth: true,
                ...field.formControlProps
              }}
              placeholder={field.placeholder}
              InputProps={{
                startAdornment: field.icon ? (
                  <InputAdornment position="start">
                    {typeof field.icon === 'string' ? getIconComponent(field.icon) : field.icon}
                  </InputAdornment>
                ) : undefined,
                ...field.InputProps
              }}
              disabled={field.disabled}
            />
          ) : (
            <MagicTextField
              label={field.label}
              name={field.name}
              value={getFieldValue(field.name)}
              onChange={field.nested ? 
                (e) => handleNestedChange(field.nested.parent, field.nested.field, e.target.value) : 
                handleTextChange
              }
              required={field.required}
              fullWidth
              placeholder={field.placeholder}
              error={touched[field.name] && Boolean(errors[field.name])}
              helperText={field.helpText || (touched[field.name] && errors[field.name])}
              type={field.type === 'number' ? 'number' : 'text'}
              InputProps={{
                startAdornment: field.icon ? (
                  <InputAdornment position="start">
                    {typeof field.icon === 'string' ? getIconComponent(field.icon) : field.icon}
                  </InputAdornment>
                ) : undefined,
                ...field.InputProps
              }}
              disabled={field.disabled}
            />
          )}
        </Grid>
      ))}
      
      {/* Render relationship field if provided */}
      {relationshipField && (
        <Grid 
          item 
          xs={relationshipField.gridSize?.xs || 12} 
          sm={relationshipField.gridSize?.sm || 6}
        >
          <MagicSelect
            name={relationshipField.name}
            label={relationshipField.label}
            value={
              typeof data[relationshipField.name] === 'object' ? 
                data[relationshipField.name]?._id || data[relationshipField.name]?.id || '' : 
                data[relationshipField.name] || ''
            }
            onChange={(name, value) => {
              // MagicSelect now passes parameters in correct order for relationship fields
              // We can simplify this handler significantly
              console.log('Relationship field change in EntityBasicInfoForm:', {
                name,
                value,
                fieldName: relationshipField.name
              });
              
              // Always call the parent's onChange handler with the received parameters
              if (onChange && typeof onChange === 'function') {
                // Pass through as received
                onChange(name, value);
              } else if (onCustomChange) {
                onCustomChange(name, value);
              } else if (setFieldValue) {
                setFieldValue(name, value);
              }
            }}
            options={relationshipField.options.map(item => ({
              value: item._id || item.id,
              label: item.name || item.title || item.label
            }))}
            error={touched[relationshipField.name] && Boolean(errors[relationshipField.name])}
            helperText={
              relationshipField.disabled ? relationshipField.disabledHelperText :
              (touched[relationshipField.name] && errors[relationshipField.name])
            }
            required={relationshipField.required}
            magical={true}
            formControlProps={{
              fullWidth: true,
            }}
            placeholder={`Select ${relationshipField.label}`}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {typeof relationshipField.icon === 'string' ? 
                    getIconComponent(relationshipField.icon) : 
                    relationshipField.icon || <PersonIcon color="action" />
                  }
                </InputAdornment>
              ),
            }}
            disabled={relationshipField.disabled}
          />
        </Grid>
      )}

      {/* Render nested fields with special handling */}
      {Object.keys(nestedFields).map(parentField => (
        nestedFields[parentField].map((field, index) => (
          <Grid 
            item 
            key={`${parentField}-${field.field}-${index}`} 
            xs={field.gridSize?.xs || 12} 
            sm={field.gridSize?.sm || 6}
          >
            {field.type === 'select' ? (
              <MagicSelect
                name={`${parentField}.${field.field}`}
                label={field.label}
                value={data[parentField]?.[field.field] || ''}
                onChange={(value) => handleNestedChange(parentField, field.field, value)}
                options={field.options || []}
                error={touched[parentField]?.[field.field] && Boolean(errors[parentField]?.[field.field])}
                helperText={touched[parentField]?.[field.field] && errors[parentField]?.[field.field]}
                required={field.required}
                magical={true}
                formControlProps={{ fullWidth: true }}
                placeholder={field.placeholder}
                InputProps={{
                  startAdornment: field.icon ? (
                    <InputAdornment position="start">
                      {typeof field.icon === 'string' ? getIconComponent(field.icon) : field.icon}
                    </InputAdornment>
                  ) : undefined,
                }}
              />
            ) : (
              <MagicTextField
                label={field.label}
                name={`${parentField}.${field.field}`}
                value={data[parentField]?.[field.field] || ''}
                onChange={(e) => handleNestedChange(parentField, field.field, 
                  field.transform ? field.transform(e.target.value) : e.target.value
                )}
                required={field.required}
                fullWidth
                placeholder={field.placeholder}
                error={touched[parentField]?.[field.field] && Boolean(errors[parentField]?.[field.field])}
                helperText={touched[parentField]?.[field.field] && errors[parentField]?.[field.field]}
                multiline={field.multiline}
                rows={field.rows}
                InputProps={{
                  startAdornment: field.icon ? (
                    <InputAdornment position="start">
                      {typeof field.icon === 'string' ? getIconComponent(field.icon) : field.icon}
                    </InputAdornment>
                  ) : undefined,
                }}
              />
            )}
          </Grid>
        ))
      ))}
    </Grid>
  );
  
  // If showHeader is false, return just the grid without FormSection
  if (!showHeader) {
    return formContent;
  }
  
  // Otherwise wrap in FormSection
  return (
    <FormSection
      title={title.includes('*') ? title : (fields.some(f => f.required) ? `${title} *` : title)}
      description={description}
      icon={icon ? (typeof icon === 'string' ? getIconComponent(icon) : icon) : <BusinessIcon />}
    >
      {formContent}
    </FormSection>
  );
};

export default EntityBasicInfoForm;